
import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../api/axios';

function AcceptInvite() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('This invite link is missing its token. Please use the link from your invitation email.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      // Backend: RecruiterInvitationController@accept (public route, no auth
      // needed — the token itself is the credential at this point).
      const response = await api.post('/recruiters/invite/accept', {
        token,
        name,
        password,
        password_confirmation: passwordConfirmation,
      });

      // Log the recruiter straight in, same as a normal login response shape.
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate('/dashboard');
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors) {
        setError(Object.values(errors).flat().join(' '));
      } else {
        setError(err.response?.data?.message || 'Could not accept invite. The link may be invalid or expired.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#EFF6FF] flex items-center justify-center px-4">
        <div className="bg-white p-8 rounded-xl shadow max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-[#0D1B3D] mb-2">Invalid Invite Link</h2>
          <p className="text-gray-500 text-sm mb-6">
            This link is missing its invite token. Please use the link from your invitation email.
          </p>
          <Link to="/login" className="text-[#2563EB] hover:underline font-medium text-sm">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EFF6FF] flex items-center justify-center px-4 py-10">
      <div className="bg-white p-6 sm:p-8 rounded-xl shadow max-w-md w-full">
        <h2 className="text-2xl font-bold text-[#0D1B3D] mb-1">Accept Your Invite</h2>
        <p className="text-gray-500 text-sm mb-6">
          Set your name and password to join the team.
        </p>

        {error && (
          <div className="bg-red-50 text-red-600 border border-red-100 p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#0D1B3D] mb-1">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              placeholder="Your full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0D1B3D] mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              placeholder="At least 8 characters"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0D1B3D] mb-1">Confirm Password</label>
            <input
              type="password"
              value={passwordConfirmation}
              onChange={(e) => setPasswordConfirmation(e.target.value)}
              required
              minLength={8}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
              placeholder="Re-enter password"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#2563EB] text-white py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Joining...' : 'Accept Invite & Join'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AcceptInvite;